/**
 * JSON reference-data import: choose a file -> preview (validate, persist nothing) -> Confirm.
 *
 * UI shell follows Financial/ChartOfAccounts/Components/BulkImportModal (hidden file input behind a
 * button, preview counts, per-row error list). It differs in one way: that screen POSTs the raw File
 * as multipart, whereas `/reference-data/<kind>/import/*` takes JSON — so the file is parsed here
 * and the rows are posted as JSON.
 */
import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowUp, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import ToastService from '@frontend/AppSettings/services/ToastService';
import { IMPORTABLE } from './catalogDescriptors';
import {
  ImportPreview,
  ImportResult,
  commitImport,
  previewImport,
  readRowsFromFile,
} from './referenceDataService';
import './BidAdminPage.css';

interface Props {
  /** Called after a successful commit so the catalog tabs refetch. */
  onImported: () => void;
}

const ImportPanel: React.FC<Props> = ({ onImported }) => {
  const [kind, setKind] = useState<string>(IMPORTABLE[0]?.importKind ?? 'equipment');
  const [fileName, setFileName] = useState<string>('');
  const [rows, setRows] = useState<unknown[] | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Bumped on every new upload/kind change; a slower in-flight preview whose generation is stale
  // must not paint itself over the current state (it would show the OLD kind's rows with rows=null,
  // leaving a Confirm button that silently does nothing).
  const generation = useRef(0);

  const reset = (): void => {
    generation.current += 1;
    setRows(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    // clear immediately so re-choosing the same file after a fix still fires onChange
    e.target.value = '';
    if (!file) return;

    reset();
    const mine = generation.current;
    setFileName(file.name);
    setBusy(true);
    try {
      const parsed = await readRowsFromFile(file);
      const previewed = await previewImport(kind, parsed);
      if (mine !== generation.current) return; // superseded (kind switched / new file chosen)
      setRows(parsed);
      setPreview(previewed);
    } catch (err: any) {
      if (mine === generation.current) setError(err?.message || 'Could not read that file.');
    } finally {
      if (mine === generation.current) setBusy(false);
    }
  };

  const handleCommit = async (): Promise<void> => {
    if (!rows) return;
    setBusy(true);
    setError(null);
    try {
      const committed = await commitImport(kind, rows);
      setResult(committed);
      setPreview(null);
      ToastService.success(
        `Imported ${committed.inserted} new and updated ${committed.updated} ${kind} rows`,
      );
      onImported();
    } catch (err: any) {
      setError(err?.message || 'Import failed. Nothing was saved.');
    } finally {
      setBusy(false);
    }
  };

  const changeKind = (next: string): void => {
    setKind(next);
    setFileName('');
    reset();
  };

  return (
    <div className="bid-admin-import" data-testid="bid-import-panel">
      <p className="bid-admin-import-help">
        Upload a JSON file of reference rows. Nothing is saved until you confirm, and importing the
        same file twice updates the existing rows instead of duplicating them.
      </p>

      <div className="bid-admin-import-controls">
        <label htmlFor="import-kind">Catalog</label>
        <select
          id="import-kind"
          className="bid-admin-input"
          value={kind}
          disabled={busy}
          onChange={(e) => changeKind(e.target.value)}
        >
          {IMPORTABLE.map((c) => (
            <option key={c.key} value={c.importKind}>
              {c.label}
            </option>
          ))}
        </select>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          className="bid-admin-file-input"
          onChange={handleFile}
          data-testid="bid-import-file"
        />
        <button
          type="button"
          className="bid-admin-btn is-primary"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
        >
          <FontAwesomeIcon icon={faFileArrowUp} /> Choose JSON file
        </button>
        {fileName && <span className="bid-admin-file-name">{fileName}</span>}
      </div>

      {error && (
        <div className="bid-admin-error" role="alert">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
        </div>
      )}

      {preview && (
        <div className="bid-admin-preview" data-testid="bid-import-preview">
          <div className="bid-admin-preview-counts">
            <strong>{preview.counts.valid}</strong> row(s) ready
            {preview.counts.invalid > 0 && (
              <>
                {' · '}
                <strong className="is-bad">{preview.counts.invalid}</strong> will be skipped
              </>
            )}
            {' · nothing has been saved yet'}
          </div>

          {preview.invalid.length > 0 && (
            <table className="bid-admin-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Problem</th>
                </tr>
              </thead>
              <tbody>
                {preview.invalid.map((bad) => (
                  <tr key={bad.index} className="is-bad-row">
                    <td>{bad.index + 1}</td>
                    <td>{bad.errors.join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {preview.valid.length > 0 && (
            <table className="bid-admin-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>{preview.key}</th>
                  <th>Values</th>
                </tr>
              </thead>
              <tbody>
                {preview.valid.slice(0, 25).map((good) => (
                  <tr key={good.index}>
                    <td>{good.index + 1}</td>
                    <td>{String(good.key ?? '')}</td>
                    <td className="bid-admin-preview-values">
                      {Object.entries(good.row)
                        .filter(([k]) => k !== preview.key)
                        .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
                        .join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {preview.valid.length > 25 && (
            <p className="bid-admin-import-help">
              Showing the first 25 of {preview.valid.length} valid rows; all of them will be imported.
            </p>
          )}

          <div className="bid-admin-form-actions">
            <button type="button" className="bid-admin-btn" onClick={reset} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="bid-admin-btn is-primary"
              onClick={handleCommit}
              disabled={busy || preview.counts.valid === 0}
              data-testid="bid-import-confirm"
            >
              {busy ? 'Importing…' : `Confirm — import ${preview.counts.valid} row(s)`}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bid-admin-result" role="status" data-testid="bid-import-result">
          Imported <strong>{result.inserted}</strong> new row(s), updated{' '}
          <strong>{result.updated}</strong>
          {result.skipped > 0 && <> , skipped <strong>{result.skipped}</strong></>}.
        </div>
      )}
    </div>
  );
};

export default ImportPanel;
